import {
  searchWorkOrdersR02,
  refetchWorkOrdersR02,
  getWorkOrderDetailR02,
  scanItemR02,
  moveMaterialsR02,
} from "src/apis/materialMoveApi";
import usePDA1071_R01Store from "./usePDA1071_R01Store";
import type {
  PDA1071_R01HeaderDto,
  PDA1071_R01SearchParams,
  PDA1071_R01Dto,
  PDA1071_R01ScanResultDto,
  PDA1071_R01MoveRequest,
  PDA1071_R01MoveResultDto,
} from "./PDA1071_R01Dto";
import { useToast } from "src/hooks/useToast";

export const usePDA1071_R01Content = () => {
  const {
    searchParams,
    setWorkOrderList,
    setSelectedWorkOrder,
    setWorkOrderDetail,
    setShowList,
    setLoading,
    resetDetail,
    workOrderDetail,
    scanValue,
    setScanValue,
    setScannedItem,
    selectedItems,
    addSelectedItem,
    clearSelectedItems,
  } = usePDA1071_R01Store();

  const { showToast } = useToast();

  const buildParams = (): PDA1071_R01SearchParams => {
    const p: PDA1071_R01SearchParams = {};
    if (searchParams.productWorkNo) p.productWorkNo = searchParams.productWorkNo;
    if (searchParams.startDate) p.startDate = searchParams.startDate;
    if (searchParams.endDate) p.endDate = searchParams.endDate;
    if (searchParams.workplaceCd) p.workplaceCd = searchParams.workplaceCd;
    if (searchParams.itemCd) p.itemCd = searchParams.itemCd;
    return p;
  };

  const fetchList = async (
    apiFn: (params?: PDA1071_R01SearchParams) => Promise<unknown>
  ) => {
    try {
      setLoading(true);
      const response = await apiFn(buildParams());
      const data =
        (response as { data?: { data?: PDA1071_R01HeaderDto[] } })?.data?.data ?? [];
      setWorkOrderList(data);
      showToast("success", `공정지시 ${data.length}건 조회됨`);
    } catch (e) {
      console.error("공정지시 조회 실패:", e);
      setWorkOrderList([]);
      showToast("error", "공정지시 조회에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchList(searchWorkOrdersR02);
  const handleRefetch = () => fetchList(refetchWorkOrdersR02);

  const handleSelectWorkOrder = async (order: PDA1071_R01HeaderDto) => {
    if (!order.productWorkNo) {
      console.error("공정지시 번호가 없습니다.");
      return;
    }
    try {
      setLoading(true);
      const response = await getWorkOrderDetailR02(order.productWorkNo, order.warehouseCd);
      const detail = (response as { data?: { data?: PDA1071_R01Dto } })?.data?.data;
      if (!detail) throw new Error("공정지시 상세 정보를 찾을 수 없습니다.");
      setSelectedWorkOrder(order);
      setWorkOrderDetail(detail);
      setShowList(false);
    } catch (e: unknown) {
      console.error("공정지시 상세 조회 실패:", e);
      const msg =
        e && typeof e === "object" && "response" in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showToast("error", msg ?? "공정지시 상세 조회에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleScanItem = async (scanValueOverride?: string): Promise<boolean> => {
    const trimmed = (scanValueOverride ?? scanValue).trim().toUpperCase();
    if (!trimmed) {
      showToast("error", "품목을 스캔하세요.");
      return false;
    }
    const productWorkNo = workOrderDetail?.header?.productWorkNo;
    if (!productWorkNo) {
      showToast("error", "공정지시를 먼저 선택하세요.");
      setScanValue("");
      return false;
    }

    try {
      setLoading(true);
      const response = await scanItemR02(productWorkNo, trimmed);
      const data = (response as { data?: { data?: PDA1071_R01ScanResultDto } })?.data?.data;
      if (!data) {
        showToast("error", "스캔 결과를 받지 못했습니다.");
        setScanValue("");
        return false;
      }
      setScannedItem(data);
      setScanValue("");
      showToast("success", `${data.itemNm} (이동가능 ${data.availableQty.toLocaleString()})`);
      return true;
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "response" in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setScanValue("");
      setScannedItem(null);
      showToast("error", msg ?? "품목 스캔에 실패했습니다.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = (moveQty: number) => {
    const { scannedItem } = usePDA1071_R01Store.getState();
    if (!scannedItem) {
      showToast("warning", "품목을 먼저 스캔하세요.");
      return;
    }
    if (!moveQty || moveQty <= 0) {
      showToast("warning", "이동수량을 입력하세요.");
      return;
    }
    if (moveQty > scannedItem.availableQty) {
      showToast(
        "error",
        `이동가능 수량을 초과했습니다. (가능 ${scannedItem.availableQty.toLocaleString()})`
      );
      return;
    }

    const exists = selectedItems.find((i) => i.itemCd === scannedItem.itemCd);
    if (exists) {
      const merged = exists.moveQty + moveQty;
      if (merged > scannedItem.availableQty) {
        showToast(
          "error",
          `누적 수량이 이동가능을 초과합니다. (가능 ${scannedItem.availableQty.toLocaleString()})`
        );
        return;
      }
      usePDA1071_R01Store.setState((s) => ({
        selectedItems: s.selectedItems.map((i) =>
          i.itemCd === scannedItem.itemCd ? { ...i, moveQty: merged } : i
        ),
      }));
      showToast("success", `${scannedItem.itemNm} 수량 합산 (${exists.moveQty} → ${merged})`);
    } else {
      addSelectedItem({
        itemCd: scannedItem.itemCd,
        itemNm: scannedItem.itemNm,
        itemSpec: scannedItem.itemSpec,
        itemUnit: scannedItem.itemUnit,
        availableQty: scannedItem.availableQty,
        moveQty,
      });
      showToast("success", `${scannedItem.itemNm} 추가됨 (${moveQty})`);
    }
    setScannedItem(null);
  };

  const handleSaveAll = async () => {
    if (!workOrderDetail) {
      showToast("error", "공정지시 정보가 없습니다.");
      return;
    }
    if (selectedItems.length === 0) {
      showToast("error", "이동할 자재가 없습니다.");
      return;
    }
    try {
      setLoading(true);
      const request: PDA1071_R01MoveRequest = {
        productWorkNo: workOrderDetail.header?.productWorkNo ?? "",
        warehouseCd: workOrderDetail.header?.warehouseCd ?? "",
        logicCenter: workOrderDetail.header?.logicCenter ?? "",
        remark: "",
        itemList: selectedItems.map((i) => ({ itemCd: i.itemCd, moveQty: i.moveQty })),
      };
      const response = await moveMaterialsR02(request);
      const result = (response as { data?: { data?: PDA1071_R01MoveResultDto } })?.data?.data;

      if (result) {
        if (result.failCount === 0) {
          showToast(
            "success",
            `자재 이동 완료 (${result.successCount}건, 총 ${result.totalMovedQty.toLocaleString()})`
          );
        } else {
          const failed = result.results
            .filter((r) => !r.success)
            .map((r) => r.itemCd)
            .join(", ");
          showToast(
            "warning",
            `${result.successCount}건 이동 / ${result.failCount}건 실패 (${failed})`
          );
        }
      }

      clearSelectedItems();
      setScannedItem(null);

      const detailResponse = await getWorkOrderDetailR02(
        workOrderDetail.header?.productWorkNo ?? "",
        workOrderDetail.header?.warehouseCd
      );
      const raw = (detailResponse as { data?: { data?: PDA1071_R01Dto } })?.data?.data;
      if (raw) setWorkOrderDetail(raw);
    } catch (e: unknown) {
      console.error("자재 이동 실패:", e);
      const msg =
        e && typeof e === "object" && "response" in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showToast("error", msg ?? "자재 이동에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (selectedItems.length === 0) return;
    if (!window.confirm(`추가된 ${selectedItems.length}건의 자재가 있습니다.\n모두 취소하시겠습니까?`)) {
      return;
    }
    clearSelectedItems();
    setScannedItem(null);
    setScanValue("");
    showToast("info", "이동 목록이 초기화되었습니다.");
  };

  const handleBackToList = () => {
    const { selectedItems: list, clearSelectedItems: clear } = usePDA1071_R01Store.getState();
    if (list.length > 0) {
      if (!window.confirm(`추가된 ${list.length}건의 자재가 있습니다.\n저장하지 않고 나가시겠습니까?`)) {
        return;
      }
      clear();
    }
    resetDetail();
    setShowList(true);
  };

  return {
    handleSearch,
    handleRefetch,
    handleSelectWorkOrder,
    handleScanItem,
    handleAddItem,
    handleSaveAll,
    handleCancel,
    handleBackToList,
  };
};
